/**
 * @fileoverview Dialog for adding or editing one caller rule on the Admin Phone tab (`CallerRules.js` owns the list). People to ring are saved by email, first person first. The phone-system rings the first person's voicemail on no answer, so the voicemail choice is saved by putting that person first. The one ring option, `ring_outside_hours`, is owned by `kc-internal-backend/functions/phone_caller_rules.py`; people on Do Not Disturb are never rung because Ringotel refuses calls to an app on DND.
 */

import React, { useState } from 'react';

import { AppButton, AppIcon, AppModal, AppSelect, AppTextInput, AppToggle } from '../shared';
import { formatPhone, personStatus } from './callerRuleFormat';


const OUTSIDE_HOURS_TITLE = "Ring outside each person's working hours";

function CallerRuleEditor({ rule, people, busy, error, onSave, onDelete, onClose }) {
  const live = new Set(people.map((person) => person.email));
  const [phone, setPhone] = useState(rule ? formatPhone(rule.phone) : '');
  const [label, setLabel] = useState(rule?.label || '');
  const [selected, setSelected] = useState(rule ? rule.recipients.filter((p) => live.has(p.email)).map((p) => p.email) : []);
  const [voicemail, setVoicemail] = useState(selected[0] || '');
  const [options, setOptions] = useState({
    ring_outside_hours: Boolean(rule?.ring_outside_hours),
  });
  const [search, setSearch] = useState('');

  const gone = rule ? rule.recipients.filter((p) => !live.has(p.email)) : [];
  const byEmail = Object.fromEntries(people.map((person) => [person.email, person]));
  const query = search.trim().toLowerCase();
  const matches = people.filter((person) => !query || person.name.toLowerCase().includes(query) || person.extension.includes(query));
  const groups = [['Attorneys', matches.filter((p) => p.is_attorney)], ['Staff', matches.filter((p) => !p.is_attorney)]];
  const voicemailTo = selected.includes(voicemail) ? voicemail : selected[0];

  const toggle = (email) => setSelected((current) => (current.includes(email) ? current.filter((item) => item !== email) : [...current, email]));

  const save = (event) => {
    event.preventDefault();
    onSave({
      phone,
      label,
      recipient_emails: [voicemailTo, ...selected.filter((email) => email !== voicemailTo)],
      ...options,
    });
  };

  const footer = (
    <>
      {error ? <p className="caller-rule-editor__error" role="alert">{error}</p> : null}
      {rule ? <AppButton variant="secondary" className="caller-rule-editor__delete" label="Delete rule" disabled={busy} onClick={onDelete} /> : null}
      <span className="caller-rule-editor__spacer" />
      <AppButton variant="secondary" label="Cancel" disabled={busy} onClick={onClose} />
      <AppButton type="submit" form="caller-rule-form" icon="pi pi-save" label="Save rule"
        disabled={busy || !phone.trim() || !selected.length} />
    </>
  );

  return (
    <AppModal visible title={rule ? 'Edit caller rule' : 'Add caller rule'} onClose={onClose} footer={footer} width={600}
      className="caller-rule-editor">
      <form id="caller-rule-form" onSubmit={save}>
        <section className="caller-rule-editor__section">
          <h3>Who's calling</h3>
          <p>The name shows on staff phones instead of the caller ID.</p>
          <div className="caller-rule-editor__fields">
            <label>
              <span>Caller number</span>
              <AppTextInput aria-label="Caller number" placeholder="(555) 555-0100" value={phone}
                onChange={(event) => setPhone(event.target.value)} />
            </label>
            <label>
              <span>Name on phones</span>
              <AppTextInput aria-label="Name on phones" placeholder="e.g. Judge Smith (optional)" maxLength={40} value={label}
                onChange={(event) => setLabel(event.target.value)} />
            </label>
          </div>
        </section>

        <section className="caller-rule-editor__section">
          <h3>
            People to ring
            {selected.length ? <span className="caller-rule-editor__count">{selected.length} selected</span> : null}
          </h3>
          {gone.length ? (
            <p className="caller-rule-editor__warning">
              {gone.map((p) => p.name || p.email).join(', ')} no longer has an active phone and will be removed when you save.
            </p>
          ) : null}
          <div className="caller-rule-picker">
            <AppTextInput aria-label="Search people" placeholder="Search people…" value={search}
              onChange={(event) => setSearch(event.target.value)} />
            <div className="caller-rule-picker__list">
              {groups.map(([title, members]) => (members.length ? (
                <div key={title} role="group" aria-label={title}>
                  <div className="caller-rule-picker__group">{title}</div>
                  {members.map((person) => {
                    const status = personStatus(person);
                    return (
                      <label key={person.email}
                        className={`caller-rule-picker__person${selected.includes(person.email) ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={selected.includes(person.email)}
                          onChange={() => toggle(person.email)} />
                        <span>{person.name}</span>
                        {status ? <span className="caller-rule-picker__status">{status}</span> : null}
                        <span className="caller-rule-picker__ext">{person.extension}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null))}
              {!matches.length ? <p className="caller-rule-picker__empty">Nobody matches “{search}”.</p> : null}
            </div>
          </div>
          {selected.length > 1 ? (
            <div className="caller-rule-editor__fallback">
              <strong>If nobody answers</strong>
              <label>
                <span>Send the caller to</span>
                <AppSelect id="caller-rule-voicemail" value={voicemailTo}
                  options={selected.map((email) => ({ value: email, label: `${byEmail[email]?.name || email}'s voicemail` }))}
                  onChange={setVoicemail} />
              </label>
            </div>
          ) : null}
        </section>

        <section className="caller-rule-editor__section">
          <h3>When they ring</h3>
          <p>Also applies when this caller dials one of these people's direct lines.</p>
          <div className="caller-rule-editor__option">
            <span className="phone-admin-setting__icon"><AppIcon icon="pi pi-moon" aria-hidden="true" /></span>
            <span className="caller-rule-editor__option-text">
              <strong>{OUTSIDE_HOURS_TITLE}</strong>
              <span>Otherwise people outside their hours get the after-hours menu instead of ringing.</span>
            </span>
            <AppToggle ariaLabel={OUTSIDE_HOURS_TITLE} checked={options.ring_outside_hours}
              onChange={({ value }) => setOptions((current) => ({ ...current, ring_outside_hours: value }))} />
          </div>
        </section>
      </form>
    </AppModal>
  );
}

export default CallerRuleEditor;
